import React, { useState, useEffect, useContext } from "react";
import "./editCharts.css";
import { DateContext } from "../navigation/navigationDrawer";
import SelectBox from "devextreme-react/select-box";
import TagBox from "devextreme-react/tag-box";
import { parseData } from "../../helpers/twxDataParser";
import { parseChartsDataModel } from "../../helpers/chartsDataModel";
import { getAggregatedData, getDataByTimeRange, saveWidgetData } from "../../services/service";
import GraphErrorPage from "../error/graph-error/graphError";
import {
  Chart,
  Series,
  Tooltip,
  CommonSeriesSettings,
  ZoomAndPan,
} from "devextreme-react/chart";
import DataGrid, { Column, Paging, Pager } from "devextreme-react/data-grid";
import PivotGrid, { FieldChooser } from "devextreme-react/pivot-grid";
import PivotGridDataSource from "devextreme/ui/pivot_grid/data_source";
import PieChart, { Legend, Label, Connector } from "devextreme-react/pie-chart";
import { Series as PieSeries } from "devextreme-react/pie-chart";
import {
  CircularGauge,
  Scale,
  Tick,
  RangeContainer,
  Range,
  ValueIndicator,
  SubvalueIndicator,
} from "devextreme-react/circular-gauge";
import { LinearGauge, Geometry } from "devextreme-react/linear-gauge";
import { BarGauge } from "devextreme-react/bar-gauge";
import { Button, TextBox } from "devextreme-react";
import Notify from 'devextreme/ui/notify';

const EditCharts = ({
  setGridItems,
  gridItems,
  gridKey,
  chartDataModelObjects,
  setchartDataModelObjects,
  machineValues,
  propertyValues,
  isDataLoadingByDate,
  setIsDataLoadingByDate
}) => {
  const chartCategories = [
    "Line",
    "Bar",
    "Side By Side Bar",
    "Table",
    "Pivot Table",
    "Doughnut Chart",
    "Radial Gauge",
    "Circular Gauge",
    "Vertical Gauge",
  ];
  const { startDate, endDate, selectedDate } = useContext(DateContext);
  const aggregationTypes = ["Sum", "Average", "Count"];
  const dimensions = ["Date", "Machines"];
  const graphErrorMessage = "Graph cannot be plotted for this data";
  const noDimensionSelection =
    "Please select at least one machine and property";
  const [hasError, setHasError] = useState(false);
  const [chartType, setChartType] = useState(chartCategories[0]);
  const [selectedDimension, setSelectedDimension] = useState(dimensions[0]);
  const [aggregationType, setAggregationType] = useState(aggregationTypes[0]);
  const [measureType, setMeasureType] = useState("timestamp");
  const [chartData, setChartData] = useState();
  const [selectedMachine, setSelectedMachine] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState([]);
  const [chartKeysList, setChartKeysList] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [pivotData, setPivotData] = useState(new PivotGridDataSource());
  const [sideBySideData, setSideBySideData] = useState([]);
  const [widgetName, setWidgetName] = useState("");

  /**
   * GET the data from thingworkx and parse it update the Machines and Property lables along with binding it with the charts data
   */
  const getTWXDataByDate = async (
    startDateValue,
    endDateValue,
    selectedMachineValue,
    selectedPropertyValue,
    aggregationType,
    dimensionType
  ) => {
    if (dimensionType === "Date") {
      let dataByDateObject = {
        startDate: startDateValue,
        endDate: endDateValue,
        machines: selectedMachineValue,
        properties: selectedPropertyValue,
        aggregationTime: selectedDate.text
      };
      return getDataByTimeRange(dataByDateObject);
    } else {
      let dataByDateObject = {
        startDate: startDateValue,
        endDate: endDateValue,
        machines: selectedMachineValue,
        properties: selectedPropertyValue,
        aggregationType,
        aggregationTime: selectedDate.text
      };
      return getAggregatedData(dataByDateObject);
    }
  };

  const parseResponseData = async () => {
    try {
      setIsDataLoadingByDate(true);
      const selectedGrid = gridItems.find((item) => item.key === gridKey);
      let selectedMachineValue = [machineValues[0].machine];
      let selectedPropertyValue = [propertyValues[0].property_name];
      let selectedChartTypeValue = chartCategories[0];
      let selectedDimensionValue = dimensions[0];
      if (selectedGrid && selectedGrid.dashboards) {
        selectedMachineValue = selectedGrid.dashboards[0].widget.machines;
        selectedPropertyValue = selectedGrid.dashboards[0].widget.property;
        selectedChartTypeValue = selectedGrid.dashboards[0].widget.chartType;
        selectedDimensionValue =
          selectedGrid.dashboards[0].widget.dimensionType;
      }
      const selectedChartModel = chartDataModelObjects.find(
        (item) => item.key === gridKey
      );

      if (selectedChartModel) {
        selectedMachineValue = selectedChartModel.machines;
        selectedPropertyValue = selectedChartModel.property;
        selectedChartTypeValue = selectedChartModel.chartType;
        selectedDimensionValue = selectedChartModel.dimensionType;
      }

      setChartType(selectedChartTypeValue);
      setSelectedMachine(selectedMachineValue);
      setSelectedProperty(selectedPropertyValue);
      setSelectedDimension(selectedDimensionValue);

      const formattedStartDate = startDate.toISOString();
      const formattedEndDate = endDate.toISOString();
      getTWXDataByDate(
        formattedStartDate,
        formattedEndDate,
        selectedMachineValue,
        selectedPropertyValue,
        aggregationType,
        selectedDimensionValue
      ).then((twxData) => {
        setIsDataLoadingByDate(false);
        updateChartData(
          twxData,
          chartType,
          selectedDimensionValue,
          selectedPropertyValue,
          selectedMachineValue
        );
      });
    } catch (error) {
      setIsDataLoadingByDate(false);
      console.error("Error fetching data:", error);
    }
  };

  /**
   * parses and adds the data model of the chart which needs to be sent to tha backend
   */
  const addChartDataModel = (
    chartType,
    dimensionType,
    selectedProperty,
    selectedMachine
  ) => {
    const selectedData = {
      machines: selectedMachine,
      property: selectedProperty,
      chartType: chartType,
      dimensionType: dimensionType,
    };
    const newChartsDataObject = parseChartsDataModel(
      selectedData,
      gridItems,
      gridKey,
      chartDataModelObjects
    );
    setchartDataModelObjects(newChartsDataObject);
  };

  const pivotTableDataSetter = (selectedDimensionType, twxData) => {
    let dataSource = new PivotGridDataSource({
      fields: [
        {
          caption: "Machine",
          dataField: "machine",
          expanded: true,
          area: "row",
        },
        {
          caption: "Property",
          dataField: "property_name",
          expanded: true,
          area: "row",
        },
        {
          dataField: "time",
          dataType: "date",
          expanded: true,
          area: "column",
        },
        {
          caption: "Value",
          dataField: "property_value",
          dataType: "number",
          summaryType: "mean",
          area: "data",
        },
      ],
      store: twxData,
    });
    return dataSource;
  };

  /**
   * updates the chart data parameter to make it into a format read by Chart.js
   */
  const updateChartData = (
    twxData,
    selectedChartType,
    selectedDimensionType,
    selectedProperties,
    selectedMachines
  ) => {
    const { chartDataSource, chartKeys } = parseData(
      twxData,
      selectedChartType,
      selectedDimensionType,
      selectedProperties,
      selectedMachines
    );
    setChartKeysList(chartKeys);
    setChartData(chartDataSource);
    if (chartDataSource.length > 0) {
      if (chartDataSource[0].timestamp) setMeasureType("timestamp");
      else setMeasureType("property");
    }
    setTableData(twxData);
    let dataSource = pivotTableDataSetter(selectedDimensionType, twxData);
    setPivotData(dataSource);
    if (
      chartDataSource.length > 0 &&
      selectedChartType === "Side By Side Bar"
    ) {
      setMeasureType("machine");
      const sideBySideDataArray = chartDataSource.map((item) => {
        const machine = selectedMachines.find((m) => item.property.includes(m));
        const propertyName = selectedProperties.find((p) =>
          item.property.includes(p)
        );
        return {
          machine: machine,
          [propertyName]: item[item.property],
        };
      });
      // Store sideBySideData in state
      setSideBySideData(sideBySideDataArray);
    } else {
      setSideBySideData([]);
    }

    addChartDataModel(
      selectedChartType,
      selectedDimensionType,
      selectedProperties,
      selectedMachines
    );
  };

  const checkForError = (chartType, dimensionType) => {
    if (
      (chartType === "Line" || chartType === "Bar") &&
      dimensionType === "Machines"
    ) {
      return false;
    } else if (chartType === "StackedBar" && dimensionType === "Date") {
      return false;
    } else if (chartType === "Side By Side Bar" && dimensionType === "Date") {
      return true;
    } else if (chartType === "Doughnut Chart" && dimensionType === "Date") {
      return true;
    } else {
      return false;
    }
  };

  /**
   * ALL the input field handlers are defined below
   */
  const handleChartTypeChange = (type) => {
    setChartType(type);
    const errorCheck = checkForError(type, selectedDimension);
    setHasError(errorCheck);
    if (!errorCheck) {
      const formattedStartDate = startDate.toISOString();
      const formattedEndDate = endDate.toISOString();
      getTWXDataByDate(
        formattedStartDate,
        formattedEndDate,
        selectedMachine,
        selectedProperty,
        aggregationType,
        selectedDimension
      ).then((twxData) => {
        updateChartData(
          twxData,
          type,
          selectedDimension,
          selectedProperty,
          selectedMachine
        );
      });
    }
  };

  const handleMachineChange = (value) => {
    let selectedMachineValue = value;
    setSelectedMachine(selectedMachineValue);
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();
    getTWXDataByDate(
      formattedStartDate,
      formattedEndDate,
      selectedMachineValue,
      selectedProperty,
      aggregationType,
      selectedDimension
    ).then((twxData) => {
      updateChartData(
        twxData,
        chartType,
        selectedDimension,
        selectedProperty,
        selectedMachineValue
      );
    });
  };

  const handlePropertyChange = (value) => {
    let selectedPropertyValue = value;
    setSelectedProperty(selectedPropertyValue);
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();
    getTWXDataByDate(
      formattedStartDate,
      formattedEndDate,
      selectedMachine,
      selectedPropertyValue,
      aggregationType,
      selectedDimension
    ).then((twxData) => {
      updateChartData(
        twxData,
        chartType,
        selectedDimension,
        selectedPropertyValue,
        selectedMachine
      );
    });
  };

  const handleDimensionChange = (dimensionType) => {
    setSelectedDimension(dimensionType);
    const errorCheck = checkForError(chartType, dimensionType);
    setHasError(errorCheck);
    if (!errorCheck) {
      const formattedStartDate = startDate.toISOString();
      const formattedEndDate = endDate.toISOString();

      getTWXDataByDate(
        formattedStartDate,
        formattedEndDate,
        selectedMachine,
        selectedProperty,
        aggregationType,
        dimensionType
      ).then((twxData) => {
        updateChartData(
          twxData,
          chartType,
          dimensionType,
          selectedProperty,
          selectedMachine
        );
      });
    }
  };

  const handleAggregationChange = (aggregationTypeValue) => {
    setAggregationType(aggregationTypeValue);
    const errorCheck = checkForError(chartType, selectedDimension);
    setHasError(errorCheck);
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();
    getTWXDataByDate(
      formattedStartDate,
      formattedEndDate,
      selectedMachine,
      selectedProperty,
      aggregationTypeValue,
      selectedDimension
    ).then((twxData) => {
      updateChartData(
        twxData,
        chartType,
        selectedDimension,
        selectedProperty,
        selectedMachine
      );
    });
  };

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const deleteGrid = () => {
    let filteredLayout = gridItems.filter((item) => item.key !== gridKey);
    setGridItems(filteredLayout);
  };

  function customizeTooltip(arg) {
    return {
      text: `${arg.seriesName} | ${arg.value}`,
    };
  }

  function customizeBarGaugeText(e) {
    return `${Object.keys(e)[0]}`;
  }

  const saveWidget = () => {
    const selectedGrid = gridItems.find((item) => item.key === gridKey);
      selectedGrid.widget = {}
      selectedGrid.widget.machines = selectedMachine;
      selectedGrid.widget.property = selectedProperty;
      selectedGrid.widget.chartType = chartType;
      selectedGrid.widget.dimensionType = selectedDimension;
      selectedGrid.widgetName = widgetName;
    if (widgetName.length === 0) {
      Notify({
        message: "Please enter a widget name",
        type: 'error',
        displayTime: 3500,
        animation: {
          show: {
            type: 'fade', duration: 400, from: 0, to: 1,
          },
          hide: { type: 'fade', duration: 40, to: 0 },
        },
      }, {
        position: 'bottom center',
        direction: 'up-push',
      });
    } else {
      const widgetDataModel = {widgetName: selectedGrid.widgetName, widget: selectedGrid.widget}
      console.log(widgetDataModel);
      saveWidgetData(widgetDataModel).then(response => {
        Notify({
          message: response.res,
          type: 'success',
          displayTime: 3500,
          animation: {
            show: {
              type: 'fade', duration: 400, from: 0, to: 1,
            },
            hide: { type: 'fade', duration: 40, to: 0 },
          },
        }, {
          position: 'bottom center',
          direction: 'up-push',
        });
      })
    }
    // console.log(selectedGrid);
  };

  const handleWidgetNameChange = (e) => {
    setWidgetName(e);
  };

  useEffect(() => {
    parseResponseData();
  }, [startDate, endDate]);

  return (
    <div className="charts-container">
      {!isDataLoadingByDate && chartData != null ? (
        <div>
          <div className="chart-selector-container">
            <div className="select-box-container">
              <div onMouseDown={stopPropagation} onTouchStart={stopPropagation}>
                <SelectBox
                  id="chart-type"
                  items={chartCategories}
                  value={chartType}
                  height={"4em"}
                  width={"10em"}
                  label="Chart Type"
                  labelMode="floating"
                  onValueChange={(e) => handleChartTypeChange(e)}
                  displayValue={chartType}
                  className="selectbox-with-min-width"
                ></SelectBox>
              </div>
              <div onMouseDown={stopPropagation} onTouchStart={stopPropagation}>
                <SelectBox
                  id="axis-type"
                  items={dimensions}
                  value={selectedDimension}
                  label="Dimensions"
                  height={"4em"}
                  width={"10em"}
                  labelMode="floating"
                  onValueChange={(e) => handleDimensionChange(e)}
                  displayValue={chartType}
                  className="selectbox-with-min-width"
                ></SelectBox>
              </div>
              <div onMouseDown={stopPropagation} onTouchStart={stopPropagation}>
                <SelectBox
                  id="agg-type"
                  items={aggregationTypes}
                  value={aggregationType}
                  height={"4em"}
                  width={"10em"}
                  disabled={selectedDimension !== "Machines"}
                  label="Aggregation Type"
                  labelMode="floating"
                  onValueChange={(e) => handleAggregationChange(e)}
                  displayValue={chartType}
                  className="selectbox-with-min-width"
                ></SelectBox>
              </div>
            </div>
            <div className="tag-box-container">
              <div onMouseDown={stopPropagation} onTouchStart={stopPropagation}>
                <TagBox
                  id="machine-type"
                  items={machineValues}
                  showSelectionControls={true}
                  maxDisplayedTags={2}
                  label="Machines"
                  height={"4em"}
                  width={"auto"}
                  labelMode="floating"
                  showMultiTagOnly={false}
                  applyValueMode="useButtons"
                  displayExpr={"machine"}
                  valueExpr={"machine"}
                  value={selectedMachine}
                  searchEnabled={true}
                  onValueChanged={(e) => handleMachineChange(e.value)}
                  className="tagbox-with-min-width"
                ></TagBox>
              </div>
              <div onMouseDown={stopPropagation} onTouchStart={stopPropagation}>
                <TagBox
                  id="property-type"
                  items={propertyValues}
                  showSelectionControls={true}
                  maxDisplayedTags={2}
                  showMultiTagOnly={false}
                  height={"4em"}
                  width={"auto"}
                  applyValueMode="useButtons"
                  label="Measures"
                  labelMode="floating"
                  displayExpr={"property_name"}
                  valueExpr={"property_name"}
                  value={selectedProperty}
                  searchEnabled={true}
                  onValueChanged={(e) => handlePropertyChange(e.value)}
                  className="tagbox-with-min-width"
                ></TagBox>
              </div>
              <div className="widget-button-container" onMouseDown={stopPropagation} onTouchStart={stopPropagation}>
                <TextBox
                  key="widget-name"
                  height={"2.5em"}
                  width={"12em"}
                  showClearButton={true}
                  placeholder="Enter Widget Name"
                  onValueChange={handleWidgetNameChange}
                  value={widgetName}
                />
                  <Button
                  key="widget-save"
                  height={"2.5em"}
                  width={"9em"}
                  text="Save As Widget"
                  stylingMode="contained"
                  className="save-button-style button-selection"
                  onClick={saveWidget}
                />
              </div>
            </div>
            <div
              className="close-button-container"
              onMouseDown={stopPropagation}
              onTouchStart={stopPropagation}
            >
              <button type="button" className="x-styling" onClick={deleteGrid}>
                X
              </button>
            </div>
          </div>
          {(selectedMachine.length === 0 || selectedProperty.length === 0) && (
            <GraphErrorPage
              errorMessage={noDimensionSelection}
            ></GraphErrorPage>
          )}
          {selectedMachine.length > 0 && selectedProperty.length > 0 && (
            <div>
              <div className="chart-header">
                {selectedProperty.join(", ")} <br />
                <span className="selected-machine">
                  {selectedMachine.join(", ")}
                </span>
              </div>
              <div
                key={gridKey}
                onMouseDown={stopPropagation}
                onTouchStart={stopPropagation}
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {hasError ? (
                  <GraphErrorPage
                    errorMessage={graphErrorMessage}
                  ></GraphErrorPage>
                ) : (
                  <>
                    {chartType === chartCategories[0] && (
                      <Chart
                        id="line-chart"
                        key="line-chart"
                        className="chart-template"
                        dataSource={chartData}
                        palette="Soft"
                      >
                        <ZoomAndPan argumentAxis="both" valueAxis="both" />
                        {chartKeysList.map((item, index) => (
                          <Series
                            key={index}
                            valueField={item}
                            argumentField={measureType}
                            type="line"
                            name={item}
                          />
                        ))}
                        <Tooltip
                          enabled={true}
                          customizeTooltip={customizeTooltip}
                        />
                        <Legend
                          visible={false}
                          verticalAlignment="bottom"
                          horizontalAlignment="center"
                          hoverMode="excludePoints"
                        />
                      </Chart>
                    )}
                    {chartType === chartCategories[1] && (
                      <Chart
                        id="bar-chart"
                        className="chart-template"
                        dataSource={chartData}
                        palette="Soft"
                      >
                        <ZoomAndPan argumentAxis="both" valueAxis="both" />
                        {chartKeysList.map((item, index) => (
                          <Series
                            key={index}
                            valueField={item}
                            argumentField={measureType}
                            type="bar"
                            name={item}
                          />
                        ))}
                        <Tooltip
                          enabled={true}
                          customizeTooltip={customizeTooltip}
                        />
                        <Legend
                          visible={false}
                          verticalAlignment="bottom"
                          horizontalAlignment="center"
                          hoverMode="excludePoints"
                        />
                      </Chart>
                    )}

                    {chartType === chartCategories[2] && sideBySideData && (
                      <Chart
                        id="chart"
                        className="chart-template"
                        dataSource={sideBySideData}
                      >
                        <CommonSeriesSettings
                          argumentField={measureType}
                          type="bar"
                        />
                        {selectedProperty.map((item, index) => (
                          <Series key={index} valueField={item} type="bar" />
                        ))}
                        <Tooltip
                          enabled={true}
                          customizeTooltip={customizeTooltip}
                        />
                        <Legend
                          visible={false}
                          verticalAlignment="bottom"
                          horizontalAlignment="center"
                          hoverMode="excludePoints"
                        />
                      </Chart>
                    )}

                    {chartType === chartCategories[3] && (
                      <DataGrid
                        id="data-grid"
                        className="chart-container"
                        dataSource={tableData}
                        showBorders={true}
                      >
                        <Column dataField="machine" dataType="string" />
                        <Column dataField="property_name" dataType="string" />
                        <Column dataField="property_value" dataType="number" />

                        <Paging defaultPageSize={12} />
                        <Pager
                          showPageSizeSelector={true}
                          allowedPageSizes={[8, 12, 20]}
                        />
                      </DataGrid>
                    )}

                    {chartType === chartCategories[4] && (
                      <PivotGrid
                        id="pivot-grid"
                        width={"auto"}
                        dataSource={pivotData}
                        allowSortingBySummary={true}
                        showColumnGrandTotals={false}
                        showRowTotals={false}
                        showRowGrandTotals={false}
                        allowSorting={true}
                        allowFiltering={true}
                        allowExpandAll={true}
                        showBorders={true}
                      >
                        <FieldChooser enabled={false} />
                      </PivotGrid>
                    )}

                    {chartType === chartCategories[5] && (
                      <PieChart
                        id="pie"
                        type="doughnut"
                        style={{
                          width: "60%",
                        }}
                        palette="Soft Pastel"
                        dataSource={chartData}
                      >
                        <PieSeries
                          valueField={"value"}
                          argumentField={measureType}
                        >
                          <Label visible={true}>
                            <Connector visible={true} />
                          </Label>
                        </PieSeries>
                        <Legend
                          visible={false}
                          margin={0}
                          horizontalAlignment="right"
                          verticalAlignment="top"
                        />
                      </PieChart>
                    )}

                    {chartType === chartCategories[6] && (
                      <div className="circular-style">
                        {chartData.map((item, index) => (
                          <CircularGauge
                            id={index}
                            key={index}
                            label={item}
                            value={item[item.property]}
                            subvalues={[40, 90]}
                          >
                            <Scale
                              startValue={0}
                              endValue={100}
                              tickInterval={10}
                            >
                              <Tick color="#536878" />
                            </Scale>
                            <RangeContainer offset={10}>
                              <Range
                                startValue={0}
                                endValue={30}
                                color="#92000A"
                              />
                              <Range
                                startValue={30}
                                endValue={70}
                                color="#E6E200"
                              />
                              <Range
                                startValue={70}
                                endValue={100}
                                color="#77DD77"
                              />
                            </RangeContainer>
                            <ValueIndicator offset={50} />
                            <SubvalueIndicator offset={-25} />
                          </CircularGauge>
                        ))}
                      </div>
                    )}

                    {chartType === chartCategories[7] && (
                      <>
                        {chartData.map((item, index) => (
                          <BarGauge
                            id={index}
                            key={index}
                            startValue={0}
                            endValue={100}
                            baseValue={0}
                            values={[item[item.property]]}
                            palette="Ocean"
                          >
                            <Label
                              indent={30}
                              customizeText={customizeBarGaugeText(item)}
                            />
                          </BarGauge>
                        ))}
                      </>
                    )}

                    {chartType === chartCategories[8] && (
                      <>
                        {chartData.map((item, index) => (
                          <LinearGauge
                            key={index}
                            id={index}
                            value={item[item.property]}
                          >
                            <Geometry orientation="vertical" />
                            <Scale
                              startValue={item[item.property] - 50}
                              endValue={item[item.property] + 50}
                            />
                          </LinearGauge>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <span>loading...</span>
      )}
    </div>
  );
};

export default EditCharts;
